import { action } from '@storybook/addon-actions';
import { useEffect, useState } from 'react';

import {
  createUID,
  menuHelpers,
  type MenuItemProps,
  type MenuProps
} from '@pega/cosmos-react-core';
import { isUserMessage } from '@pega/cosmos-react-work';
import type {
  GenAIMessageProps,
  MessageProps,
  UtilitiesGenAICoachProps,
  Reaction,
  State
} from '@pega/cosmos-react-work';

import type { UtilitiesLayoutDemoProps } from '../UtilitiesLayout/UtilitiesLayout.stories';

import {
  conversationHistorySummaryData,
  coachOptionsMock,
  genAICoachMessageMockData,
  initialSuggestedMessagesMockData,
  suggestionsMock
} from './GenAICoach.mocks';

const useGenAIcoach: (args: UtilitiesLayoutDemoProps) => UtilitiesGenAICoachProps | null = ({
  enableGenAICoach,
  showGenAICoachErrorMessage
}) => {
  const [messages, setMessages] = useState<GenAIMessageProps[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [progressiveLoading, setProgressiveLoading] = useState(false);
  const [initialSuggestedMessages, setInitialSuggestedMessages] = useState<MessageProps[]>([]);
  const [selectedCoach, setSelectedCoach] = useState<MenuItemProps>(coachOptionsMock[0]);
  const [selectedItemInHistory, setSelectedItemInHistory] = useState(
    conversationHistorySummaryData[0].conversations[1].id
  );
  const [inFullPage, setInFullPage] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [historyViewData, setHistoryViewData] = useState(conversationHistorySummaryData);

  const sendMessage: (message: MessageProps) => void = message => {
    setHistoryViewData(prev => {
      let movedItem;
      prev.forEach((section, index) => {
        const chatThreadIndex = section.conversations.findIndex(
          item => item.id === selectedItemInHistory
        );
        if (chatThreadIndex !== -1) {
          movedItem = section.conversations.splice(chatThreadIndex, 1)[0];
          if (section.conversations.length === 0) {
            prev.splice(index, 1);
          }
        }
      });
      if (movedItem) prev[0].conversations = [movedItem, ...prev[0].conversations];
      return prev;
    });
    setMessages([
      ...messages,
      {
        ...message,
        from: 'user'
      }
    ]);
  };

  const handleFeedback = (messageId: MessageProps['id'], feedback?: string) => {
    if (feedback) action('Submit')(`${messageId}:${feedback}`);
    else action('Cancel')(`${messageId}`);
  };

  const handleReaction = (id: string, updatedReaction: Reaction) => {
    setMessages(prevMessages =>
      prevMessages.map(message =>
        message.id === id
          ? {
              ...message,
              feedback: {
                reaction: updatedReaction,
                onReaction: handleReaction,
                onFeedbackComplete: handleFeedback
              }
            }
          : message
      )
    );
  };

  useEffect(() => {
    if (messages.length > 0 && isUserMessage(messages[messages.length - 1])) {
      setTimeout(() => {
        const random = Math.floor(Math.random() * 2);
        const genAiMessage = genAICoachMessageMockData[random];
        if (!isUserMessage(genAiMessage))
          setMessages([
            ...messages,
            {
              ...genAiMessage,
              id: createUID(),
              coachName: selectedCoach?.primary,
              feedback: {
                onReaction: handleReaction,
                reaction: undefined,
                onFeedbackComplete: handleFeedback
              }
            }
          ]);
      }, 500);
    }
  }, [messages.length]);

  const initializeCoach = () => {
    setLoading(true);
    setTimeout(() => {
      setInitialSuggestedMessages(initialSuggestedMessagesMockData);
      setMessages([]);
      setLoading(false);
    }, 1000);
    setShowHistory(false);
  };

  useEffect(() => {
    initializeCoach();
  }, []);

  if (enableGenAICoach)
    return {
      loading,
      coachOptions: coachOptionsMock,
      error: showGenAICoachErrorMessage ? 'Error retreiving the data' : undefined,
      onCoachChange: (id: MenuProps['id']) => {
        if (id) {
          const selected = menuHelpers.getItem(coachOptionsMock, id);
          if (selected) setSelectedCoach(selected);
        }
        initializeCoach();
      },
      messages,
      initialSuggestedMessages,
      onSend: sendMessage,
      suggestions: suggestionsMock,
      onOpen: action('Gen AI open'),
      onClose: action('Gen AI closed'),
      onStateChange: (state: State) => action('Gen AI state updated')(state),
      onLayoutChange: (inFullPageArg: boolean) => {
        setInFullPage(inFullPageArg);
      },
      actions: {
        items: [
          {
            id: '1',
            text: 'New chat',
            onClick: () => {
              initializeCoach();
            }
          },
          {
            id: '2',
            text: 'History',
            onClick: () => {
              setShowHistory(true);
            }
          }
        ]
      },
      conversationHistory: showHistory
        ? {
            data: historyViewData,
            onDismiss: () => {
              setShowHistory(false);
            },
            loading: progressiveLoading,
            loadMore: () => {
              setProgressiveLoading(true);
              setTimeout(() => {
                setProgressiveLoading(false);
              }, 5000);
            },
            onItemClick: conversationId => {
              setSelectedItemInHistory(conversationId);

              if (!inFullPage) setShowHistory(false);
              setMessages(genAICoachMessageMockData);
            },
            selectedItemId: selectedItemInHistory
          }
        : undefined
    };
  return null;
};

export default useGenAIcoach;
